import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  BookOpen, ChevronDown, ChevronUp, MessageSquare, Target,
  Lightbulb, AlertTriangle, CheckCircle, ArrowRight, Star,
  Users, Euro, Zap, Shield, Clock, TrendingUp, Heart,
  Sparkles, Award, PhoneCall, HelpCircle,
} from 'lucide-react';

const GUIDE_SECTIONS = [
  {
    id: 'intro',
    titre: "Présentation Bureau Agricole",
    icon: BookOpen,
    color: 'emerald',
    contenu: [
      {
        sous_titre: "Pitch en 30 secondes",
        texte: "Bureau Agricole est le premier ERP 100% conçu pour les exploitations agricoles. Une seule plateforme avec IA intégrée qui gère la comptabilité, les parcelles, les stocks, la traçabilité, les RH, et le commercial. L'agriculteur gagne en moyenne 8h par semaine sur ses tâches administratives.",
      },
      {
        sous_titre: "Ce qui nous différencie",
        points: [
          "IA intégrée (Valentine) : une assistante de direction qui comprend le métier agricole",
          "Agents spécialisés : comptable, technique, commercial, RH — chacun expert dans son domaine",
          "Multi-workspaces : une seule interface pour gérer plusieurs exploitations/sociétés",
          "Conçu PAR des agriculteurs, POUR des agriculteurs",
          "Mise à jour continue — pas besoin de racheter des licences chaque année",
        ],
      },
      {
        sous_titre: "Chiffres clés",
        points: [
          "8h/semaine gagnées en moyenne",
          "30% de réduction des erreurs comptables",
          "Conformité PAC et traçabilité assurées",
          "Support humain + IA disponible 7j/7",
        ],
      },
    ],
  },
  {
    id: 'questions_decouverte',
    titre: "Questions de Découverte",
    icon: HelpCircle,
    color: 'blue',
    contenu: [
      {
        sous_titre: "Comprendre l'exploitation",
        questions: [
          "Quelle est la taille de votre exploitation ? Combien d'hectares ?",
          "Quelles sont vos activités principales ? (Viticulture, élevage, grandes cultures...)",
          "Combien de collaborateurs travaillent sur l'exploitation ?",
          "Avez-vous plusieurs structures juridiques ? (EARL, GAEC, SCI...)",
          "Comment gérez-vous votre comptabilité aujourd'hui ? En interne ou avec un cabinet ?",
        ],
      },
      {
        sous_titre: "Identifier les douleurs",
        questions: [
          "Combien de temps passez-vous par semaine sur l'administratif ?",
          "Quels sont les logiciels que vous utilisez actuellement ? Êtes-vous satisfait ?",
          "Avez-vous déjà eu des problèmes de traçabilité ou de conformité ?",
          "Comment suivez-vous vos stocks et vos achats de produits phyto ?",
          "Les déclarations PAC, c'est un casse-tête pour vous ?",
          "Vos collaborateurs ont-ils accès facilement aux infos dont ils ont besoin ?",
        ],
      },
      {
        sous_titre: "Évaluer la maturité digitale",
        questions: [
          "Utilisez-vous déjà un logiciel de gestion ? Lequel ?",
          "Êtes-vous à l'aise avec les outils numériques ?",
          "Avez-vous un smartphone / tablette pour le terrain ?",
          "Vos données sont actuellement sur papier, Excel, ou un logiciel ?",
        ],
      },
    ],
  },
  {
    id: 'modules',
    titre: "Modules & Fonctionnalités",
    icon: Zap,
    color: 'purple',
    contenu: [
      {
        sous_titre: "Valentine — L'assistante IA",
        points: [
          "Vocale ET texte : parlez-lui comme à une collègue",
          "Lit vos emails, gère votre agenda, fait vos récaps",
          "Analyse vos photos de parcelles (maladies, carences)",
          "Délègue aux agents spécialisés automatiquement",
          "Briefing quotidien avec les priorités du jour",
        ],
        argument: "Imaginez avoir une secrétaire ultra-compétente, disponible 24h/24, qui connaît par cœur votre exploitation.",
      },
      {
        sous_titre: "Comptabilité Agricole",
        points: [
          "Saisie comptable simplifiée (factures, achats, ventes)",
          "TVA agricole automatique (taux réduit, régime forfaitaire)",
          "Rapprochement bancaire intelligent",
          "Alertes proactives de Pierre (agent comptable IA)",
          "Export comptable compatible avec tous les cabinets",
        ],
        argument: "Notre IA comptable détecte les anomalies AVANT votre expert-comptable. Fini les mauvaises surprises.",
      },
      {
        sous_titre: "Gestion Parcellaire & Technique",
        points: [
          "Carte interactive des parcelles avec données IGN",
          "Plan de fumure, traitements, rotations",
          "Base EPHY intégrée (13 500+ produits phyto)",
          "Registre phytosanitaire conforme",
          "Analyse satellite (stress hydrique, NDVI)",
        ],
        argument: "Tout votre parcellaire dans la poche. Scannez une plante, l'IA diagnostique et propose le traitement.",
      },
      {
        sous_titre: "Stocks & Traçabilité",
        points: [
          "Suivi en temps réel des stocks (phyto, engrais, semences)",
          "Alertes de réapprovisionnement automatiques",
          "Traçabilité complète lot par lot",
          "Conformité contrôles (PAC, MSA, DGAL)",
        ],
        argument: "En cas de contrôle, tout est prêt en 2 clics. Plus de stress.",
      },
      {
        sous_titre: "RH & Équipe",
        points: [
          "Planning des équipes, pointage, absences",
          "Gestion saisonniers et contrats TESA",
          "Formation et habilitations",
          "Fiches de paie simplifiées (lien MSA)",
        ],
        argument: "Gérez votre équipe aussi bien que vos parcelles. Les saisonniers arrivent ? Tout est prêt.",
      },
      {
        sous_titre: "Commercial & CRM",
        points: [
          "Suivi clients et prospects",
          "Devis et factures en quelques clics",
          "Vente directe (bon de livraison, étiquetage)",
          "Statistiques commerciales temps réel",
        ],
        argument: "Pour ceux qui font de la vente directe : du client au bon de livraison, tout est intégré.",
      },
    ],
  },
  {
    id: 'objections',
    titre: "Réponses aux Objections",
    icon: Shield,
    color: 'red',
    contenu: [
      {
        objection: "C'est trop cher pour mon exploitation",
        reponse: "À combien estimez-vous le temps que vous passez sur l'administratif par semaine ? À 50€/h, 8h c'est 400€. Notre abonnement à partir de 100€/mois vous fait économiser 300€/mois minimum. Et on ne parle même pas des erreurs comptables évitées.",
        tip: "Ramenez toujours au coût horaire de l'agriculteur.",
      },
      {
        objection: "Je ne suis pas à l'aise avec l'informatique",
        reponse: "C'est justement pour ça qu'on a créé Valentine ! Vous lui parlez comme à une collègue — à voix haute, en français. Pas besoin de cliquer partout. Et on vous forme personnellement.",
        tip: "Proposez une démo vocale en direct pour rassurer.",
      },
      {
        objection: "J'ai déjà un logiciel de compta",
        reponse: "Bureau Agricole ne remplace pas votre comptable. Il le complète et lui facilite la vie. On exporte dans tous les formats. Et surtout, on ajoute tout ce que votre logiciel comptable ne fait pas : parcellaire, stocks, traçabilité, IA.",
        tip: "Insistez sur la complémentarité, pas le remplacement.",
      },
      {
        objection: "J'ai peur de perdre mes données",
        reponse: "Vos données sont sauvegardées en continu sur des serveurs sécurisés en France. On peut aussi sauvegarder sur votre Google Drive. Et vos données vous appartiennent — export complet possible à tout moment.",
        tip: "Le mot 'France' rassure toujours. Mentionnez la conformité RGPD.",
      },
      {
        objection: "Je veux d'abord en parler à mon associé / comptable",
        reponse: "Bien sûr ! Je peux préparer un document résumé pour votre associé. Et pourquoi ne pas l'inviter à un essai gratuit de 3 jours ? Il pourra tester par lui-même.",
        tip: "Toujours proposer l'essai gratuit comme porte d'entrée.",
      },
      {
        objection: "Je n'ai pas le temps en ce moment (moisson, vendanges...)",
        reponse: "C'est justement dans ces moments-là que Bureau Agricole est le plus utile ! Valentine peut gérer vos tâches admin pendant que vous êtes au champ. On peut démarrer l'installation maintenant et vous former quand vous aurez un creux.",
        tip: "Retournez l'objection : plus ils sont débordés, plus ils ont besoin de l'outil.",
      },
      {
        objection: "Les autres logiciels agricoles me suffisent",
        reponse: "Lesquels utilisez-vous ? (Écouter) Le problème c'est que vous jongler entre plusieurs outils. Bureau Agricole unifie tout : un seul login, une seule interface, une seule IA qui a la vision d'ensemble de votre exploitation.",
        tip: "Faites la liste de tous leurs outils actuels et montrez que BA les remplace tous.",
      },
    ],
  },
  {
    id: 'closing',
    titre: "Techniques de Closing",
    icon: Target,
    color: 'amber',
    contenu: [
      {
        sous_titre: "L'essai gratuit",
        texte: "Toujours proposer l'essai gratuit de 3 jours. C'est l'arme absolue. L'agriculteur teste, il adopte. Taux de conversion essai → client : 65%.",
        script: "\"Écoutez, le mieux c'est que vous testiez par vous-même. Je vous active un accès gratuit de 3 jours, sans engagement. Si ça vous plaît, on continue. Sinon, pas de souci.\"",
      },
      {
        sous_titre: "Le résumé de valeur",
        texte: "Avant de closer, résumez les 3 douleurs identifiées et comment BA les résout.",
        script: "\"Donc si je résume : vous perdez 10h/semaine sur l'admin, votre traçabilité est sur papier, et vos stocks ne sont pas suivis. Avec Bureau Agricole, Valentine gère l'admin, le registre phyto est automatique, et les stocks sont en temps réel. On y va ?\"",
      },
      {
        sous_titre: "L'urgence naturelle",
        texte: "Utilisez les saisons comme levier.",
        script: "\"Si on lance l'installation maintenant, tout sera en place avant la prochaine campagne. Sinon, c'est encore une saison avec les mêmes galères.\"",
      },
      {
        sous_titre: "Le closing alternatif",
        texte: "Ne demandez pas SI ils veulent souscrire, mais QUEL palier.",
        script: "\"Vu la taille de votre exploitation, je vous recommande le palier Moyenne. Mais si vous préférez commencer plus léger, le palier Petite Exploitation vous donne déjà l'essentiel. Qu'est-ce qui vous convient le mieux ?\"",
      },
      {
        sous_titre: "L'ambassadeur",
        texte: "Si le prospect est enthousiaste, proposez le programme ambassadeur.",
        script: "\"Au fait, on a un programme parrainage. Si vous recommandez Bureau Agricole à un collègue et qu'il souscrit, vous avez 1 mois gratuit tous les deux. C'est gagnant-gagnant !\"",
      },
    ],
  },
  {
    id: 'parcours_vente',
    titre: "Parcours de Vente Idéal",
    icon: ArrowRight,
    color: 'teal',
    contenu: [
      {
        sous_titre: "Étape 1 — Premier Contact (5 min)",
        points: [
          "Se présenter chaleureusement",
          "Identifier le type d'exploitation en 2-3 questions",
          "Écouter plus que parler (règle 70/30)",
          "Repérer la douleur principale",
        ],
      },
      {
        sous_titre: "Étape 2 — Découverte (10 min)",
        points: [
          "Poser les questions de découverte (section dédiée)",
          "Reformuler pour montrer qu'on a compris",
          "Commencer à noter les modules pertinents",
          "Ne PAS vendre encore, juste écouter",
        ],
      },
      {
        sous_titre: "Étape 3 — Démo ciblée (15 min)",
        points: [
          "Montrer UNIQUEMENT les fonctionnalités qui répondent à SES douleurs",
          "Faire parler Valentine en direct (effet waouh)",
          "Montrer une analyse de photo parcelle si pertinent",
          "Laisser l'agriculteur toucher l'interface",
        ],
      },
      {
        sous_titre: "Étape 4 — Chiffrage (5 min)",
        points: [
          "Utiliser le Configurateur Sur-Mesure",
          "Montrer le palier recommandé avec les packs métiers",
          "Comparer au coût horaire actuel de l'admin",
          "Proposer l'engagement annuel (-15%) si intéressé",
        ],
      },
      {
        sous_titre: "Étape 5 — Closing (5 min)",
        points: [
          "Résumer les 3 bénéfices clés",
          "Proposer l'essai gratuit 3 jours",
          "Si hésitation → proposer de revenir avec l'associé",
          "Toujours repartir avec une action : essai, RDV, devis envoyé",
        ],
      },
      {
        sous_titre: "Étape 6 — Suivi (J+1 à J+30)",
        points: [
          "J+1 : Email de remerciement + récap",
          "J+3 : Appel de suivi si essai en cours",
          "J+7 : Relance si devis non répondu",
          "J+14 : Proposition alternative si toujours pas de réponse",
          "J+30 : Dernier contact, passage en 'froid' si pas de réponse",
        ],
      },
    ],
  },
  {
    id: 'tarifs',
    titre: "Grille Tarifaire & Arguments",
    icon: Euro,
    color: 'green',
    contenu: [
      {
        sous_titre: "Petite Exploitation (100-150€/mois)",
        points: [
          "Idéal pour : < 50 ha, 1-2 personnes, activité unique",
          "Inclus : Compta, Parcellaire, Valentine, 1 workspace",
          "Argument : \"Moins cher qu'un stagiaire à temps partiel, et disponible 24h/24\"",
        ],
      },
      {
        sous_titre: "Moyenne Exploitation (500€/mois)",
        points: [
          "Idéal pour : 50-200 ha, 3-10 personnes, multi-activités",
          "Inclus : Tous les modules + 3 workspaces + formations",
          "Argument : \"Le prix d'une demi-journée de prestation comptable par mois, pour un outil disponible en permanence\"",
        ],
      },
      {
        sous_titre: "Grosse Exploitation (1200€/mois)",
        points: [
          "Idéal pour : > 200 ha, 10+ personnes, groupement",
          "Inclus : Illimité + support dédié + personnalisations",
          "Argument : \"À ce niveau, le ROI est garanti dès le 2ème mois. On parle de milliers d'euros d'économies annuelles\"",
        ],
      },
      {
        sous_titre: "Options supplémentaires",
        points: [
          "Packs métiers : +0 à +30€/mois selon le pack",
          "Collaborateurs additionnels : +29€/mois/personne",
          "Workspaces supplémentaires : +49 à +179€/mois",
          "Engagement annuel : -15% | 2 ans : -20%",
          "Programme ambassadeur : 1 mois offert par parrainage converti",
        ],
      },
    ],
  },
];

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-800' },
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800' },
};

export default function GuideVenteTab() {
  const [expandedSections, setExpandedSections] = useState(['intro']);

  const toggleSection = (id) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setExpandedSections(GUIDE_SECTIONS.map(s => s.id));
  const collapseAll = () => setExpandedSections([]);

  return (
    <div className="space-y-4" data-testid="guide-vente-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-600" />
            Guide de Vente Bureau Agricole
          </h3>
          <p className="text-sm text-muted-foreground">
            Tout ce qu'il faut savoir pour vendre Bureau Agricole. 7 sections, de la découverte au closing.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>Tout ouvrir</Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>Tout fermer</Button>
        </div>
      </div>

      {/* Quick Nav */}
      <div className="flex flex-wrap gap-2">
        {GUIDE_SECTIONS.map(section => {
          const colors = COLOR_MAP[section.color];
          const Icon = section.icon;
          return (
            <Button
              key={section.id}
              variant="outline"
              size="sm"
              className={`text-xs ${expandedSections.includes(section.id) ? colors.badge : ''}`}
              onClick={() => {
                if (!expandedSections.includes(section.id)) toggleSection(section.id);
                document.getElementById(`guide-${section.id}`)?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Icon className="w-3 h-3 mr-1" />{section.titre}
            </Button>
          );
        })}
      </div>

      {/* Sections */}
      <ScrollArea className="h-[600px]">
        <div className="space-y-3 pr-3">
          {GUIDE_SECTIONS.map(section => {
            const isExpanded = expandedSections.includes(section.id);
            const colors = COLOR_MAP[section.color];
            const Icon = section.icon;

            return (
              <Card
                key={section.id}
                id={`guide-${section.id}`}
                className={`border-l-4 ${isExpanded ? `${colors.border} ${colors.bg}` : 'border-l-gray-200'}`}
                data-testid={`guide-section-${section.id}`}
              >
                <CardHeader
                  className="pb-2 cursor-pointer"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className={`text-sm flex items-center gap-2 ${isExpanded ? colors.text : ''}`}>
                      <Icon className="w-4 h-4" />
                      {section.titre}
                      <Badge className={`text-[10px] ${colors.badge}`}>
                        {section.contenu.length} sous-sections
                      </Badge>
                    </CardTitle>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="space-y-4 pt-0">
                    {section.contenu.map((item, i) => (
                      <div key={i} className="p-3 bg-white/80 rounded-lg border">
                        {/* Sous-titre ou Objection */}
                        {item.sous_titre && (
                          <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                            <Star className="w-3 h-3 text-amber-500" />
                            {item.sous_titre}
                          </h4>
                        )}
                        {item.objection && (
                          <div className="mb-2">
                            <Badge className="bg-red-100 text-red-700 text-xs mb-1">
                              <AlertTriangle className="w-3 h-3 mr-1" />Objection
                            </Badge>
                            <p className="font-semibold text-sm text-red-800">"{item.objection}"</p>
                          </div>
                        )}

                        {/* Texte */}
                        {item.texte && <p className="text-sm text-gray-700 leading-relaxed">{item.texte}</p>}

                        {/* Réponse objection */}
                        {item.reponse && (
                          <div className="mt-2 p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
                            <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />Réponse recommandée
                            </p>
                            <p className="text-sm text-emerald-800">{item.reponse}</p>
                          </div>
                        )}

                        {/* Points */}
                        {item.points && (
                          <ul className="space-y-1 mt-2">
                            {item.points.map((point, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm">
                                <CheckCircle className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Questions */}
                        {item.questions && (
                          <ul className="space-y-1.5 mt-2">
                            {item.questions.map((q, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm">
                                <MessageSquare className="w-3 h-3 mt-0.5 text-blue-500 shrink-0" />
                                <span className="italic">"{q}"</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Script */}
                        {item.script && (
                          <div className="mt-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                              <PhoneCall className="w-3 h-3" />Script
                            </p>
                            <p className="text-sm text-amber-900 italic">{item.script}</p>
                          </div>
                        )}

                        {/* Argument commercial */}
                        {item.argument && (
                          <div className="mt-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                            <p className="text-xs font-semibold text-purple-700 mb-0.5 flex items-center gap-1">
                              <Lightbulb className="w-3 h-3" />Argument clé
                            </p>
                            <p className="text-sm text-purple-800 italic">"{item.argument}"</p>
                          </div>
                        )}

                        {/* Tip */}
                        {item.tip && (
                          <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-500">
                            <Sparkles className="w-3 h-3 mt-0.5 text-amber-500" />
                            <span>Astuce : {item.tip}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
